import useDocumentTitle from '@/components/Helmet'
import { Button } from '@/components/ui/button'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '@/lib/axiosInstance'
import { useCustomLoader } from '@/components/Loader'
import { useToast } from '@/hooks/use-toast'
import { getPropertyById, getPropertyData, getVendorById, getVendorsData } from '@/apis/apis'
import GlobalAutocomplete from '@/components/material/Autocomplete'
import { useLocation, useParams } from 'wouter'
import dayjs from 'dayjs'
import { ArrowLeft } from 'lucide-react'

const formSchema = z.object({
    title: z.string().min(1, "Title is required"),
    type: z.enum(['Preventive', 'Corrective', 'Inspection'], {
        errorMap: () => ({ message: "Type is required" }),
    }),
    scheduledDate: z.any().refine((val) => val != null, {
        message: "Scheduled Date is required",
    }),
    status: z.enum(['scheduled', 'in_progress', 'completed', 'cancelled'], {
        errorMap: () => ({ message: "Status is required" }),
    }),
    vendor: z.string().min(1, "Vendor is required"),
    propertyId: z.object({
        _id: z.string(),
        name: z.string(),
        units: z.array(z.string()),
    }, {
        errorMap: () => ({ message: "Property is required" }),
    }),
    unitId: z.string().min(1, "Unit ID is required"),
    description: z.string().min(1, "Description is required").max(500, "Description cannot be more than 500 characters"),
});


const AddEdit = () => {
    useDocumentTitle("Add Tasks");
    const loader = useCustomLoader();
    const [_, navigate] = useLocation();
    const { toast } = useToast();
    const { id } = useParams();
    const [vendors, setVendors] = useState<any>({});
    const [vendorSearch, setVendorSearch] = useState("");
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [searchProperty, setSearchProperty] = useState("");
    const [property, setProperty] = useState<any>({});
    const [selectedProperty, setSelectedProperty] = useState(null);

    const { control, formState: { errors }, handleSubmit, watch, reset, setValue } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: '',
            type: '',
            scheduledDate: dayjs(),
            status: '',
            vendor: '',
            propertyId: {
                _id: '', name: '', units: []
            },
            unitId: [""],
            description: '',
        }
    })

    // Preload property/vendor when editing
    useEffect(() => {
        if (id) {
            const fetchTaskById = async () => {
                try {
                    const res = await axiosInstance.get(`/api/tasks/${id}`);
                    const data = res.data;

                    reset(data);

                    if (data.scheduledDate) {
                        setValue("scheduledDate", dayjs(data.scheduledDate));
                    }

                    // Preload property
                    if (data.propertyId) {
                        const propertyRes = await getPropertyById(data.propertyId._id);
                        setProperty((prev: any) => {
                            const safePrev = prev && Array.isArray(prev.data) ? prev : { count: 0, data: [] };
                            if (!safePrev.data.find((p: any) => p._id === propertyRes._id)) {
                                return {
                                    count: safePrev.count + 1,
                                    data: [propertyRes, ...safePrev.data],
                                };
                            }
                            return safePrev;
                        });
                        setSelectedProperty(propertyRes);
                        setValue("propertyId", propertyRes);
                    }

                    // Preload vendor
                    if (data.vendor) {
                        const vendorRes = await getVendorById(data.vendor._id);
                        setVendors((prev: any) => {
                            const safePrev = prev && Array.isArray(prev.vendors) ? prev : { count: 0, vendors: [] };
                            if (!safePrev.vendors.find((v: any) => v._id === vendorRes._id)) {
                                return {
                                    count: safePrev.count + 1,
                                    vendors: [vendorRes, ...safePrev.vendors],
                                };
                            }
                            return safePrev;
                        });
                        setSelectedVendor(vendorRes);
                        setValue("vendor", vendorRes._id);
                    }
                } catch (error) {
                    console.error(error);
                }
            };

            fetchTaskById();
        }
    }, [id]);

    // 🔍 Search property
    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const data = await getPropertyData({
                    filters: { active: true, search: searchProperty },
                    page: 0,
                    rowsPerPage: 10,
                });

                setProperty((prev: any) => {
                    const safePrev = prev && Array.isArray(prev.data) ? prev : { count: 0, data: [] };
                    // merge instead of replace
                    const merged = [...safePrev.data];
                    data.data.forEach((item: any) => {
                        if (!merged.find((p) => p._id === item._id)) {
                            merged.push(item);
                        }
                    });
                    return { count: merged.length, data: merged };
                });
            } catch (error: any) {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: error?.response?.data?.message || "Something went wrong",
                });
            }
        };

        const timer = setTimeout(fetchProperty, 600);
        return () => clearTimeout(timer);
    }, [searchProperty]);

    // 🔍 Search vendors
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const data = await getVendorsData({
                    page: 0,
                    limit: 10,
                    search: vendorSearch,
                    active: true,
                    specialties: watch("type"),
                });

                setVendors((prev: any) => {
                    const safePrev = prev && Array.isArray(prev.vendors) ? prev : { count: 0, vendors: [] };
                    // merge instead of replace
                    const merged = [...safePrev.vendors];
                    data.vendors.forEach((item: any) => {
                        if (!merged.find((v) => v._id === item._id)) {
                            merged.push(item);
                        }
                    });
                    return { count: merged.length, vendors: merged };
                });
            } catch (error: any) {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: error?.response?.data?.message || "Something went wrong",
                });
            }
        };

        const timer = setTimeout(fetchVendors, 600);
        return () => clearTimeout(timer);
    }, [vendorSearch, watch("type")]);


    const onSubmit = async (payload: any) => {
        loader(true);
        try {
            let response = {
                data: {
                    success: true,
                    message: ""
                }
            };
            payload.propertyId = payload.propertyId._id;
            if (id) {
                response = await axiosInstance.put(`/api/tasks/${id}`, payload);
            }
            else {

                response = await axiosInstance.post("/api/tasks", payload);
            }
            if (response?.data?.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: response?.data.message,
                })
                navigate("/task");
            }
            else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response?.data.message,
                })
            }

        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
        finally {
            loader(false);
        }

    }
    return (
        <div>
            <Box className='flex justify-between items-center mb-4'>
                <Button variant='default' onClick={() => navigate("/task")}><ArrowLeft /> Back</Button>
                <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 24 }} >{id ? "Edit" : "Add"} Tasks</Typography>
                <div></div>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="title"
                            control={control}
                            render={({ field }) => (
                                <TextField type='text' fullWidth label="Title" {...field} placeholder="Please Enter Title" />
                            )}
                        />
                        {
                            errors.title && <span style={{ color: 'red' }}>{String(errors.title.message)}</span>
                        }
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="type">Type</InputLabel>
                                    <Select fullWidth label="Type" {...field} labelId="type" id="type">
                                        <MenuItem value="Preventive">Preventive</MenuItem>
                                        <MenuItem value="Corrective">Corrective</MenuItem>
                                        <MenuItem value="Inspection">Inspection</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {
                            errors.type && <span style={{ color: 'red' }}>{String(errors.type.message)}</span>
                        }
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="scheduledDate"
                            control={control}
                            render={({ field }) => (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Scheduled Date"
                                        {...field}
                                        format='MM-DD-YYYY'
                                    />

                                </LocalizationProvider>
                            )}
                        />
                        {
                            errors.scheduledDate && <span style={{ color: 'red' }}>{String(errors.scheduledDate.message)}</span>
                        }
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="status">Status</InputLabel>
                                    <Select {...field} labelId="status" id="status" label="Status">
                                        <MenuItem value="scheduled">Scheduled</MenuItem>
                                        <MenuItem value="in_progress">In Progress</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="cancelled">Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />

                        {
                            errors.status && <span style={{ color: 'red' }}>{String(errors.status.message)}</span>
                        }
                    </Grid>

                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="vendor"
                            control={control}
                            render={({ field }) => (
                                <GlobalAutocomplete
                                    label="Vendor"
                                    options={vendors?.vendors || []}
                                    getOptionLabel={(option: any) => option.name}
                                    value={selectedVendor}
                                    onChange={(newValue) => {
                                        setSelectedVendor(newValue);
                                        field.onChange(newValue?._id);
                                    }}
                                    onInputChange={(value) => {
                                        setVendorSearch(value);
                                    }}
                                    placeholder="Please Enter Vendor"
                                />
                            )}
                        />
                        {
                            errors.vendor && <span style={{ color: 'red' }}>{String(errors.vendor.message)}</span>
                        }
                    </Grid>

                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="propertyId"
                            control={control}
                            render={({ field }) => (
                                <GlobalAutocomplete
                                    label="Property"
                                    options={property?.data || []}
                                    getOptionLabel={(option: any) => option.name}
                                    value={selectedProperty}
                                    onChange={(newValue) => {
                                        setSelectedProperty(newValue);
                                        field.onChange(newValue);
                                    }}
                                    onInputChange={(value) => {
                                        setSearchProperty(value);
                                    }}
                                    placeholder="Please Select Property"
                                />
                            )}
                        />
                        {
                            errors.propertyId && <span style={{ color: 'red' }}>{String(errors.propertyId.message)}</span>
                        }
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="unitId"
                            control={control}
                            render={({ field }) => (
                                <GlobalAutocomplete
                                    label="Unit"
                                    options={watch("propertyId")?.units || []}
                                    value={field.value || null}
                                    onChange={(newValue) => field.onChange(newValue ?? "")}
                                    placeholder="Select or enter unit"
                                    getOptionLabel={(option: any) => option || ''}
                                    error={!!errors.unitId}
                                    helperText={!watch("propertyId")?._id ? "Please select property first" : ''}
                                />
                            )}
                        />
                        {
                            watch("propertyId") && errors.unitId && <span style={{ color: 'red' }}>{String(errors.unitId.message)}</span>
                        }
                    </Grid>

                    <Grid size={{ sm: 12, xs: 12 }}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <TextField multiline rows={4} fullWidth label="Description" {...field} placeholder="" />
                            )}
                        />
                        {
                            errors.description && <span style={{ color: 'red' }}>{String(errors.description.message)}</span>
                        }
                    </Grid>
                    <Grid size={{ sm: 12, xs: 12 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button type="button" variant="error" onClick={() => navigate('/task')} >Cancel</Button>
                            <Button type="submit" variant="default" >{id ? "Update" : "Save"}</Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </div>
    )
}

export default AddEdit